import { useQuery } from '@tanstack/react-query';

import APIGateway from '@/api';
import {
  AgentActionsResponse, MetricsSummary, ModelInvocationsResponse, TimeRange
} from '@/types/observability';

export const observabilityKeys = {
  all: [ 'observability' ] as const,
  summary: ( agent: string, timeRange: TimeRange ) => [
    ...observabilityKeys.all, 'summary', agent, timeRange
  ] as const,
  modelInvocations: ( agent: string, page: number, pageSize: number, timeRange: TimeRange ) => [
    ...observabilityKeys.all, 'model-invocations', agent, page, pageSize, timeRange
  ] as const,
  agentActions: ( agent: string, page: number, pageSize: number, timeRange: TimeRange ) => [
    ...observabilityKeys.all, 'agent-actions', agent, page, pageSize, timeRange
  ] as const
};

export function useMetricsSummaryQuery(
  agent: string | undefined,
  timeRange: TimeRange = '7d'
) {
  return useQuery( {
    queryKey: observabilityKeys.summary( agent!, timeRange ),
    queryFn: async(): Promise<MetricsSummary> => {
      if ( !agent ) throw new Error( 'Agent name required' );
      return APIGateway.observability.metricsSummaryGet( agent, timeRange );
    },
    enabled: !!agent,
    staleTime: 30000,
    retry: 1
  } );
}

export function useModelInvocationsQuery(
  agent: string | undefined,
  page: number = 1,
  pageSize: number = 20,
  timeRange: TimeRange = '7d'
) {
  return useQuery( {
    queryKey: observabilityKeys.modelInvocations( agent!, page, pageSize, timeRange ),
    queryFn: async(): Promise<ModelInvocationsResponse> => {
      if ( !agent ) throw new Error( 'Agent name required' );
      return APIGateway.observability.modelInvocationsGet( agent, page, pageSize, timeRange );
    },
    enabled: !!agent,
    staleTime: 30000,
    retry: 1
  } );
}

export function useAgentActionsQuery(
  agent: string | undefined,
  page: number = 1,
  pageSize: number = 20,
  timeRange: TimeRange = '7d'
) {
  return useQuery( {
    queryKey: observabilityKeys.agentActions( agent!, page, pageSize, timeRange ),
    queryFn: async(): Promise<AgentActionsResponse> => {
      if ( !agent ) throw new Error( 'Agent name required' );
      return APIGateway.observability.agentActionsGet( agent, page, pageSize, timeRange );
    },
    enabled: !!agent,
    staleTime: 30000,
    retry: 1
  } );
}
